import { useState, useRef } from 'react';
import { CustomerDTO, useCustomers } from '../../../hooks/useCustomer';
import TitlePage from '../../../components/TitlePage';
import { Column, TableComponent } from '../../../components/TableComponent';
import CreateUpdate from './components/createUpdate';
import { Input } from '@/components/ui/input';
import Delete from './components/delete';
import { Filter, Search } from 'lucide-react';
import { debounce } from 'lodash';
import LoadingCircle from '@/components/LoadingCircle';
import {
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Select } from '@/components/ui/select';
import { useGetEmployeeRoleIsSeller } from '@/hooks/useEmployee';
import { Button } from '@/components/ui/button';
import { useProfileContext } from '@/hooks/useProfileContext';
import Detail from './components/detail';
import { checkIsPermissionWithScreen, formatDate } from '@/lib/utils';
import HistoryChange from './components/historyChange';
import TextUserInactive from '@/components/TextUserInactive';
import { userStatus } from '@/api/employee';

const limit = 10;
const Customer = () => {
  const { profile } = useProfileContext();
  const isSeller = profile?.role?.key === 'marketing';

  const searchRef = useRef<HTMLInputElement>(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [sellerId, setSellerId] = useState<string | null>(
    isSeller ? profile?.id : null
  );
  const [idHistorySeller, setIdHistorySeller] = useState<string | null>(null);

  const [isOpenHistorySeller, setIsOpenHistorySeller] = useState(false);

  const { data, isLoading, isFetching } = useCustomers({
    page,
    limit,
    search,
    sellerId,
  });

  const { data: employeesData } = useGetEmployeeRoleIsSeller(isSeller);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <LoadingCircle />
      </div>
    );
  }

  const handleCloseHistorySeller = () => {
    setIsOpenHistorySeller(false);
    setIdHistorySeller(null);
  };

  const columns: Column<CustomerDTO>[] = [
    {
      header: 'Mã KH',
      cell: (item) => {
        return <div>KH-{item.id}</div>;
      },
    },
    {
      header: 'Tên khách hàng',
      accessorKey: 'fullName',
      cell: (item) => {
        return (
          <div className="font-medium text-luxury-900">{item.fullName}</div>
        );
      },
    },
    {
      header: 'Email',
      accessorKey: 'email',
    },
    {
      header: 'Số điện thoại',
      accessorKey: 'phoneNumber',
    },
    {
      header: 'Tên vợ/chồng',
      accessorKey: 'spouseName',
    },
    {
      header: 'Số điện thoại vợ/chồng',
      accessorKey: 'phoneFamily',
    },
    {
      header: 'Nhân viên phụ trách',
      accessorKey: 'seller',
      cell: (item) => {
        return (
          <div className="flex flex-col">
            {item.seller.name} <TextUserInactive status={item.seller.status} />
          </div>
        );
      },
    },
    {
      header: 'Lịch sử thay đổi nhân viên',
      accessorKey: 'seller',
      cell: (item) => {
        return (
          <div
            className="text-blue-500 cursor-pointer text-center"
            onClick={() => {
              setIsOpenHistorySeller(true);
              setIdHistorySeller(item.id);
            }}
          >
            Xem lịch sử
          </div>
        );
      },
    },
    {
      header: 'Địa chỉ làm việc',
      accessorKey: 'workAddress',
      cell: (item) => {
        return (
          <div className="w-[300px] whitespace-pre-line break-words">
            {item.address}
          </div>
        );
      },
    },
    {
      header: 'Địa chỉ nhà ở',
      accessorKey: 'address',
      cell: (item) => {
        return (
          <div className="w-[300px] whitespace-pre-line break-words">
            {item.address}
          </div>
        );
      },
    },
    {
      header: 'Ngày sinh',
      accessorKey: 'dateOfBirth',
      cell: (item) => {
        return <div>{formatDate(item.dateOfBirth)}</div>;
      },
    },
    {
      header: 'Người tạo',
      accessorKey: 'createdBy',
      cell: (item) => {
        return <div>{item.createdBy.name}</div>;
      },
    },
    {
      header: 'Hành động',
      cell: (item) => {
        return (
          <div className="flex items-center gap-2">
            {checkIsPermissionWithScreen(
              'customers',
              'read',
              profile?.role?.permissions
            ) && <Detail customer={item} />}
            {checkIsPermissionWithScreen(
              'customers',
              'update',
              profile?.role?.permissions
            ) && (
              <CreateUpdate
                customer={item}
                employeesData={employeesData || []}
              />
            )}
            {checkIsPermissionWithScreen(
              'customers',
              'delete',
              profile?.role?.permissions
            ) && (
              <Delete
                customer={item}
                setPage={() => {
                  if (data?.customers?.length === 1 && page === data?.pages) {
                    setPage(page - 1);
                  }
                }}
              />
            )}
          </div>
        );
      },
    },
  ];

  const debouncedSearch = debounce((value: string) => {
    setPage(1);
    setSearch(value);
  }, 500);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    debouncedSearch(e.target.value);
  };

  return (
    <div className="luxury-theme min-h-screen">
      <div className="flex items-center justify-between bg-white p-4 border-b border-luxury-200 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="text-2xl  text-luxury-900">
            <TitlePage />
          </div>
        </div>
        {checkIsPermissionWithScreen(
          'customers',
          'create',
          profile?.role?.permissions
        ) && <CreateUpdate employeesData={employeesData || []} />}
      </div>

      <div className="flex items-center justify-end bg-white p-4 border-b border-luxury-200 shadow-sm gap-4">
        <div className="flex items-center gap-2">
          {!isSeller && (
            <Select
              onValueChange={(value) => {
                setSellerId(value);
                setPage(1);
              }}
              value={sellerId || ''}
            >
              <SelectTrigger className="w-[250px] luxury-input">
                <SelectValue placeholder="Chọn nhân viên phụ trách" />
              </SelectTrigger>
              <SelectContent className="border-luxury-200">
                {employeesData
                  ?.filter((employee) => employee.status === userStatus.ACTIVE)
                  .map((employee) => (
                    <SelectItem key={employee.id} value={employee.id}>
                      {employee.name}
                      <TextUserInactive status={employee.status} />
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          )}
        </div>
        <div className="relative">
          <Input
            className="w-[300px] pr-10 luxury-input"
            placeholder="Tìm kiếm theo sđt, tên hoặc email"
            onChange={handleSearch}
            ref={searchRef}
          />
          <Search className="w-4 h-4 absolute right-4 top-1/2 -translate-y-1/2 text-luxury-500" />
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            className="luxury-button-outline"
            onClick={() => {
              if (!isSeller) {
                setSellerId(null);
              }
              setPage(1);
              setSearch('');
              if (searchRef.current) {
                searchRef.current.value = '';
              }
            }}
          >
            <Filter className="w-4 h-4 mr-2" />
            Xoá lọc
          </Button>
        </div>
      </div>

      <div className="px-6 py-6 relative">
        {isFetching && (
          <div className="absolute inset-0 bg-white/60 backdrop-blur-sm flex items-center justify-center z-10">
            <LoadingCircle />
          </div>
        )}
        <TableComponent
          data={data?.customers || []}
          columns={columns}
          pageSize={limit}
          totalPages={data?.pages || 0}
          onPageChange={setPage}
          currentPage={page}
          className="luxury-table"
        />

        <HistoryChange
          idHistorySeller={idHistorySeller}
          onClose={handleCloseHistorySeller}
          isOpen={isOpenHistorySeller}
        />
      </div>
    </div>
  );
};

export default Customer;
